import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, BookOpen, ChevronDown, ChevronRight, Loader2, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { articlesForPage, audiencesFor, breadcrumb, buildTree, countArticles, findArticle, imageUrl, loadKb, resolveArticleLink, searchArticles } from "@/lib/kb";

// Help pages are shown in all three app shells (worker = dark, office/portal = light).
const THEMES = {
  dark: {
    page: "text-slate-200", title: "text-white", muted: "text-slate-400", link: "text-emerald-300",
    card: "border-white/10 bg-white/5 hover:bg-white/10", heading: "text-white", strong: "text-white",
    code: "bg-white/10 text-slate-100", th: "border-white/15 bg-white/10", td: "border-white/10",
    hr: "border-white/10", quote: "border-white/20 text-slate-400",
  },
  light: {
    page: "text-slate-700", title: "text-slate-900", muted: "text-slate-500", link: "text-sky-700",
    card: "border-slate-200 bg-white hover:bg-slate-50", heading: "text-slate-800", strong: "text-slate-800",
    code: "bg-slate-100 text-slate-700", th: "border-slate-300 bg-slate-50", td: "border-slate-200",
    hr: "border-slate-200", quote: "border-slate-300 text-slate-500",
  },
};

function KbMarkdown({ article, kb, basePath, audiences, theme }) {
  const t = THEMES[theme];
  const components = {
    h2: (p) => <h2 className={`text-[15px] font-semibold mt-5 mb-1.5 ${t.heading}`} {...p} />,
    h3: (p) => <h3 className={`text-sm font-semibold mt-3 mb-1 ${t.heading}`} {...p} />,
    p: (p) => <p className="mb-3 leading-relaxed" {...p} />,
    ul: (p) => <ul className="list-disc pl-5 mb-3 space-y-1" {...p} />,
    ol: (p) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...p} />,
    strong: (p) => <strong className={`font-semibold ${t.strong}`} {...p} />,
    hr: () => <hr className={`my-4 ${t.hr}`} />,
    blockquote: (p) => <blockquote className={`border-l-2 pl-3 my-3 ${t.quote}`} {...p} />,
    code: (p) => <code className={`text-[12px] rounded px-1 py-0.5 ${t.code}`} {...p} />,
    table: (p) => <div className="overflow-auto my-3"><table className="w-full border-collapse text-xs" {...p} /></div>,
    th: (p) => <th className={`border px-2 py-1 text-left font-semibold ${t.th}`} {...p} />,
    td: (p) => <td className={`border px-2 py-1 align-top ${t.td}`} {...p} />,
    // Screenshots are files in src/kb/_images; tap one to open it full size.
    img: ({ src, alt }) => {
      const url = imageUrl(article.slug, src) || src;
      return (
        <a href={url} target="_blank" rel="noreferrer" className="block my-3">
          <img src={url} alt={alt || ""} loading="lazy" className="max-w-full rounded-lg border border-black/10 shadow-sm" />
        </a>
      );
    },
    a: ({ href, children }) => {
      const slug = resolveArticleLink(article.slug, href);
      if (slug) {
        // Link to another article — only if this reader is allowed to see it.
        const target = findArticle(kb, slug, audiences);
        if (!target) return <>{children}</>;
        return <Link to={`${basePath}/${target.slug}`} className={`underline ${t.link}`}>{children}</Link>;
      }
      return <a href={href} target="_blank" rel="noreferrer" className={`underline ${t.link}`}>{children}</a>;
    },
  };
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{article.body}</ReactMarkdown>;
}

// Remembers which sections / parent articles the reader opened (this browser tab only),
// so coming back from an article doesn't collapse everything again.
const OPEN_KEY = "kb-open";
function readOpen() {
  try { return new Set(JSON.parse(sessionStorage.getItem(OPEN_KEY) || "[]")); } catch { return new Set(); }
}
function rememberOpen(id, open) {
  try {
    const set = readOpen();
    if (open) set.add(id); else set.delete(id);
    sessionStorage.setItem(OPEN_KEY, JSON.stringify([...set]));
  } catch { /* storage unavailable: fine, just not remembered */ }
}

// A row linking to an article. When `nested`, an article with sub-articles gets a separate
// "N related" toggle (collapsed by default) so the title itself always just opens the article.
function ArticleRow({ a, basePath, t, trail, nested }) {
  const subs = nested ? a.children : [];
  const [open, setOpen] = useState(() => readOpen().has(a.slug));
  const toggle = () => { setOpen(!open); rememberOpen(a.slug, !open); };
  return (
    <div>
      <div className={`flex items-stretch rounded-lg border overflow-hidden transition-colors ${t.card}`}>
        <Link to={`${basePath}/${a.slug}`} data-testid={`help-link-${a.slug.replace(/\//g, "-")}`}
          className={`flex-1 min-w-0 flex items-center justify-between gap-3 px-3 py-2.5 text-sm font-medium ${t.title}`}>
          <span>
            {a.title}
            {trail && <span className={`block text-[11px] font-normal ${t.muted}`}>{trail}</span>}
          </span>
          {subs.length === 0 && <ChevronRight className={`h-4 w-4 shrink-0 ${t.muted}`} />}
        </Link>
        {subs.length > 0 && (
          <button type="button" onClick={toggle} aria-expanded={open} data-testid={`help-toggle-${a.slug.replace(/\//g, "-")}`}
            aria-label={`${open ? "Hide" : "Show"} ${subs.length} related ${subs.length === 1 ? "article" : "articles"} for ${a.title}`}
            className={`shrink-0 flex items-center gap-1.5 border-l px-3 text-[11px] font-medium hover:bg-black/5 ${t.hr} ${t.muted}`}>
            {subs.length} related
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {open && subs.length > 0 && (
        <div className={`ml-4 mt-1.5 mb-1 pl-3 border-l space-y-1.5 ${t.hr}`}>
          {subs.map((c) => renderArticle(c, { basePath, t, nested }))}
        </div>
      )}
    </div>
  );
}
// (A function returning the component, so ArticleRow never renders itself directly —
// a since-removed dev Babel plugin overflowed the stack on self-rendering components.)
function renderArticle(a, opts) { return <ArticleRow key={a.slug} a={a} {...opts} />; }

// One collapsible section: a card with its title and a short summary, collapsed by default.
// Plain recursive function for the same reason as above.
function renderSection(node, { basePath, t, depth }) {
  return (
    <details key={node.path} open={readOpen().has(node.path)} onToggle={(e) => rememberOpen(node.path, e.currentTarget.open)}
      className={`group mb-2 ${depth > 0 ? "ml-3 pl-3 border-l " + t.hr : ""}`} data-testid={`help-section-${node.path.replace(/\//g, "-")}`}>
      <summary className={`list-none [&::-webkit-details-marker]:hidden cursor-pointer select-none flex items-center justify-between gap-3 rounded-lg border px-3 py-3 transition-colors ${t.card}`}>
        <span className="min-w-0">
          <span className={`flex items-baseline gap-2 text-sm font-semibold ${t.title}`}>
            {node.title}
            <span className={`text-[11px] font-normal ${t.muted}`}>{countArticles(node)} {countArticles(node) === 1 ? "article" : "articles"}</span>
          </span>
          {node.description && <span className={`block text-xs mt-0.5 ${t.muted}`}>{node.description}</span>}
        </span>
        <ChevronRight className={`h-4 w-4 shrink-0 transition-transform group-open:rotate-90 ${t.muted}`} />
      </summary>
      <div className="space-y-2 pt-2 pb-1">
        {node.articles.map((a) => renderArticle(a, { basePath, t, nested: true }))}
        {node.children.map((c) => renderSection(c, { basePath, t, depth: depth + 1 }))}
      </div>
    </details>
  );
}

export default function Help({ basePath, theme = "light" }) {
  const { user, isWorker, isPortal } = useAuth();
  const slug = useParams()["*"];
  const [params] = useSearchParams();
  const forPage = params.get("page");
  const nav = useNavigate();
  const t = THEMES[theme];
  const audiences = audiencesFor({ isWorker, isPortal, role: user?.role });
  const [kb, setKb] = useState(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  useEffect(() => { loadKb().then(setKb).catch(() => setFailed(true)); }, []);

  if (failed) return <div className={`px-4 py-8 text-sm text-center ${t.muted}`}>Couldn't load the help articles. Check your connection and try again.</div>;
  if (!kb) return <div className="flex justify-center py-12"><Loader2 className={`h-5 w-5 animate-spin ${t.muted}`} /></div>;

  if (slug) {
    const article = findArticle(kb, slug, audiences);
    if (!article) return <Navigate to={basePath} replace />;
    // Old link (moved article) → canonical address.
    if (article.slug !== slug) return <Navigate to={`${basePath}/${article.slug}`} replace />;
    const trail = breadcrumb(kb, article);
    return (
      <div className={`px-4 py-5 max-w-2xl mx-auto ${t.page}`} data-testid="help-article">
        <button onClick={() => nav(basePath)} className={`flex items-center gap-1 text-xs mb-3 ${t.muted}`} data-testid="help-back">
          <ArrowLeft className="h-3.5 w-3.5" /> All help articles
        </button>
        {trail.length > 0 && <div className={`text-[11px] mb-1 ${t.muted}`} data-testid="help-breadcrumb">{trail.join(" › ")}</div>}
        <h1 className={`text-lg font-semibold ${t.title}`}>{article.title}</h1>
        {article.lastReviewed && <div className={`text-[11px] mt-0.5 mb-4 ${t.muted}`}>Last reviewed {article.lastReviewed}</div>}
        <div className="text-sm"><KbMarkdown article={article} kb={kb} basePath={basePath} audiences={audiences} theme={theme} /></div>
        {article.children.length > 0 && (
          <div className={`mt-6 pt-4 border-t ${t.hr}`} data-testid="help-sub-articles">
            <div className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${t.muted}`}>Related help</div>
            <div className="space-y-2">{article.children.map((c) => renderArticle(c, { basePath, t }))}</div>
          </div>
        )}
      </div>
    );
  }

  if (forPage) {
    const list = articlesForPage(kb, forPage, audiences);
    if (list.length > 0) {
      return (
        <div className={`px-4 py-5 max-w-2xl mx-auto ${t.page}`} data-testid="help-page-list">
          <button onClick={() => nav(basePath)} className={`flex items-center gap-1 text-xs mb-3 ${t.muted}`}><ArrowLeft className="h-3.5 w-3.5" /> All help articles</button>
          <h1 className={`text-lg font-semibold mb-3 ${t.title}`}>Help for this page</h1>
          <div className="space-y-2">{list.map((a) => renderArticle(a, { basePath, t, trail: breadcrumb(kb, a).join(" › ") }))}</div>
        </div>
      );
    }
  }

  const tree = buildTree(kb, audiences);
  const results = searchArticles(kb, query, audiences);
  const searching = query.trim().length > 0;
  return (
    <div className={`px-4 py-5 max-w-2xl mx-auto ${t.page}`} data-testid="help-index">
      <h1 className={`text-lg font-semibold flex items-center gap-2 ${t.title}`}><BookOpen className="h-5 w-5" /> Help &amp; guides</h1>
      <div className="relative mt-3">
        <Search className={`h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 ${t.muted}`} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search help (e.g. location blocked)" data-testid="help-search"
          className={`w-full rounded-lg border pl-9 pr-3 py-2 text-sm bg-transparent outline-none focus:ring-2 focus:ring-sky-400/50 ${t.card}`} />
      </div>
      {tree.length === 0 && <div className={`text-sm mt-6 ${t.muted}`}>No help articles yet.</div>}
      {searching ? (
        <div className="mt-4 space-y-2" data-testid="help-results">
          {results.length === 0 && <div className={`text-sm ${t.muted}`}>No articles match "{query.trim()}".</div>}
          {results.map((a) => renderArticle(a, { basePath, t,
            trail: [audiences.length > 1 ? { worker: "Workers", participant: "Participants", office: "Office", admin: "Admins" }[a.audience] : null, ...breadcrumb(kb, a)].filter(Boolean).join(" › ") }))}
        </div>
      ) : tree.map((g) => (
        <div key={g.audience} className="mt-5">
          {tree.length > 1 && <div className={`text-[11px] font-bold uppercase tracking-wider mb-1 ${t.muted}`}>{g.label}</div>}
          <div className="space-y-2 mb-2">
            {g.root.articles.map((a) => renderArticle(a, { basePath, t, nested: true }))}
          </div>
          {g.root.children.map((c) => renderSection(c, { basePath, t, depth: 0 }))}
        </div>
      ))}
    </div>
  );
}

// Participant portal has no router shell of its own, so wrap Help in a minimal header.
export function PortalHelp() {
  const { isPortal } = useAuth();
  if (!isPortal) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/portal" className="flex items-center gap-1 text-sm text-slate-600" data-testid="portal-help-back"><ArrowLeft className="h-4 w-4" /> Back to portal</Link>
        </div>
      </header>
      <Help basePath="/portal/help" theme="light" />
    </div>
  );
}
